import { generateRandomUUID } from "@/Utils/UniqueId";
import {
  categorySchema,
  videoTypeSchema,
  type IActivityCategory,
  type IMeasurementsType,
} from "@medoma/models/activities";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import type {
  INewActivity,
  INewAdminTaskActivity,
  INewClinicVisitActivity,
  INewHomeVisitActivity,
  INewPatientMeasurementTaskActivity,
  INewPatientTaskActivity,
  INewPhoneCallActivity,
  INewVideoActivity,
  IWeekday,
} from "@/api/Activities";

/**
 * Three valid shapes returned by the form-side time helpers; spreading any of
 * these onto a category-specific payload completes a valid `INewXActivity`.
 */
type ITimeFields =
  | { time: string }
  | {
      times: string[] | "AnyTimeOfDay";
      interval: number;
      endDate?: Date;
    }
  | {
      times: string[] | "AnyTimeOfDay";
      days: IWeekday[];
      endDate?: Date;
    };

type ICommonFields = Pick<
  INewActivity,
  "title" | "description" | "duration" | "hidden" | "startDate" | "timespan"
>;

/**
 * `patientId` is `null` only for AdminTask activities created without a
 * patient; for every other category the caller is expected to have validated
 * that a patient id is present before invoking this function.
 */
export type IBuildNewActivityInput = ICommonFields & {
  category: IActivityCategory;
  patientId: string | null;
  // React Hook Form returns `false` for a checkbox group with no selection.
  requiredCompetences: IMedicalCompetence[] | false | null | undefined;
  doubleStaffing: boolean | null | undefined;
  measurements: IMeasurementsType[] | null | undefined;
  timeFields: ITimeFields;
};

export const buildNewActivity = (
  input: IBuildNewActivityInput,
): INewActivity => {
  const {
    category,
    patientId,
    requiredCompetences,
    doubleStaffing,
    measurements,
    timeFields,
  } = input;

  const commonFields = {
    activityId: generateRandomUUID(),
    title: input.title.trim(),
    description: input.description?.trim() ?? "",
    duration: input.duration,
    hidden: input.hidden,
    startDate: input.startDate,
    timespan: input.timespan,
  };

  const competences = Array.isArray(requiredCompetences)
    ? requiredCompetences
    : [];

  if (category === categorySchema.enum.AdminTask) {
    const activity: INewAdminTaskActivity = {
      ...commonFields,
      patientId,
      category: categorySchema.enum.AdminTask,
      requiredCompetences: competences,
      ...timeFields,
    };
    return activity;
  }

  if (patientId === null) {
    throw new Error(`patientId is required for category: ${category}`);
  }

  switch (category) {
    case categorySchema.enum.VideoCall: {
      const activity: INewVideoActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.VideoCall,
        requiredCompetences: competences,
        type: videoTypeSchema.enum.DigitalVisit,
        ...timeFields,
      };
      return activity;
    }
    case categorySchema.enum.ClinicVisit: {
      const activity: INewClinicVisitActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.ClinicVisit,
        requiredCompetences: competences,
        ...timeFields,
      };
      return activity;
    }
    case categorySchema.enum.HomeVisit: {
      const activity: INewHomeVisitActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.HomeVisit,
        requiredCompetences: competences,
        doubleStaffing: doubleStaffing === true,
        ...timeFields,
      };
      return activity;
    }
    case categorySchema.enum.PatientTask: {
      const activity: INewPatientTaskActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.PatientTask,
        ...timeFields,
      };
      return activity;
    }
    case categorySchema.enum.PatientMeasurementTask: {
      const activity: INewPatientMeasurementTaskActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.PatientMeasurementTask,
        measurements: measurements ?? [],
        ...timeFields,
      };
      return activity;
    }
    case categorySchema.enum.PhoneCall: {
      const activity: INewPhoneCallActivity = {
        ...commonFields,
        patientId,
        category: categorySchema.enum.PhoneCall,
        requiredCompetences: competences,
        ...timeFields,
      };
      return activity;
    }
    default:
      throw category satisfies never;
  }
};
